import type { RecordTarget } from '../comms/booking/dispatchLink';
import { subcategoryOf } from './metricMeta';
import { formatTenantMoney } from '../../lib/tenantCurrency';
import { ourRef } from '../../lib/reservationRef';

/**
 * The rows behind a figure.
 *
 * Every point dispatch answers with carries a `data` array of the records that
 * made it up, and their SHAPE differs per subcategory — a jobs bucket is
 * `{id}`, an invoice bucket is `{id, billingAddress, invoiceTotal, due}`, a
 * customers bucket is a person. This is the port of the dashboard's
 * `metric_record_formatter.dart`, which is the one place that knows which is
 * which, and it must stay one: a record read with the wrong shape renders a
 * customer's name as a job number on a screen whose whole job is to say what a
 * number is made of.
 *
 * Two rules the Dart version states and this keeps:
 *
 * - **An unknown shape falls back to key/value**, never to nothing. dispatch
 *   adds buckets without telling us, and a row of `id: 91  •  total: 240` is
 *   still an answer; an empty list would read as "this figure came from
 *   nowhere".
 * - **Payables sort biggest-balance-first.** Those buckets carry plenty of $0
 *   rows, and read in dispatch's own order they bury the amounts that matter.
 */

// The target type lives with the routes that address it — one idea of what a
// record IS, rather than two that drift.
export type { RecordTarget };

export interface MetricRecordRow {
  title: string;
  subtitle: string | null;
  /** The figure this row contributed, already formatted. */
  trailing: string | null;
  /** The dispatch id to link at, or null when the row names nothing addressable. */
  linkId: string | null;
  linkTarget: RecordTarget | null;
}

type Raw = Record<string, unknown>;

const JOB_ID_ONLY = new Set([
  'NEW_JOBS',
  'PENDING_JOBS',
  'CUSTOMER_JOBS',
  'GUEST_JOBS',
  'AFFILIATE_JOBS',
  'FARM_IN_JOBS',
  'IN_HOUSE_JOBS',
  'HOURLY_JOBS',
  'ONE_WAY_JOBS',
  'REJECTED_JOBS',
  'CANCELLED_JOBS',
  'DONE_JOBS',
  'NO_SHOW_JOBS',
]);

const QUOTE_ID_ONLY = new Set(['ACCEPTED_QUOTES', 'NEW_QUOTES', 'PENDING_QUOTES', 'DECLINED_QUOTES']);
const QUOTE_WITH_TOTAL = new Set(['QUOTES_TOTAL', 'QUOTES_ACCEPTED_TOTAL', 'QUOTES_DECLINED_TOTAL']);
const DRIVER_ID_ONLY = new Set(['PENDING_DRIVERS']);
const INVOICE_BASIC = new Set(['NEW_INVOICES', 'CLOSED_INVOICES', 'INVOICE_FINALIZED']);

/** Payables records are jobs carrying a balance — `{id, total}`, as JOBS_TOTAL is. */
const PAYABLES = new Set([
  'PAYABLES_TOTAL',
  'PAYABLES_BY_PICKUP_DATE',
  'PAYABLES_CHAUFFEUR',
  'PAYABLES_AFFILIATE',
]);

const normalize = (value: string): string =>
  subcategoryOf(value)?.code ??
  String(value || '')
    .trim()
    .replace(/-/g, '_')
    .replace(/\s/g, '_')
    .toUpperCase();

const text = (value: unknown): string => (value === null || value === undefined ? '' : String(value).trim());

const num = (value: unknown): number | null => {
  if (value === null || value === undefined) return null;
  const parsed = typeof value === 'number' ? value : Number(String(value));
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * A record's own amount, to the cent.
 *
 * Deliberately NOT `money()` from analyticsFormat, which rounds to the whole
 * unit: that is right for a headline figure and wrong here, where the rows
 * are individual invoices and balances and the cents are what a person
 * reconciles against. The dashboard's own record list shows them too.
 */
const cash = (value: unknown): string | null => {
  const amount = num(value);
  return amount === null ? null : formatTenantMoney(amount, 2);
};

/** `-` when the record names no id, which is what suppresses the link. */
const recordId = (raw: Raw, primary: string, fallback?: string): string => {
  const first = text(raw[primary]);
  if (first) return first;
  if (fallback) {
    const second = text(raw[fallback]);
    if (second) return second;
  }
  return '-';
};

const linkable = (id: string): string | null => (id === '-' ? null : id);

const personOrCompany = (raw: Raw): string | null => {
  const first = text(raw.firstName);
  if (first) {
    const last = text(raw.lastName);
    return last ? `${first} ${last}` : first;
  }
  for (const key of ['name', 'companyName', 'company']) {
    const value = text(raw[key]);
    if (value) return value;
  }
  return null;
};

const affiliateName = (raw: Raw): string | null => {
  for (const key of ['companyName', 'name', 'company']) {
    const value = text(raw[key]);
    if (value) return value;
  }
  return personOrCompany(raw);
};

const contactLine = (raw: Raw, includeState = false): string | null => {
  const parts: string[] = [];
  for (const key of ['email', 'phone']) {
    const value = text(raw[key]);
    if (value) parts.push(value);
  }
  if (includeState) {
    const state = text(raw.state);
    if (state) parts.push(state);
  }
  return parts.length ? parts.join(' • ') : null;
};

const billingAddress = (raw: Raw): string | null => text(raw.billingAddress) || null;

/**
 * The invoice line under the number: the billing address, and the total when it
 * differs from the figure already on the right. Repeating it would be the same
 * money twice on one row.
 */
const invoiceSubtitle = (raw: Raw, highlightField: string, amountField: string): string | null => {
  const parts: string[] = [];
  const billing = billingAddress(raw);
  if (billing) parts.push(billing);
  const highlight = num(raw[highlightField]);
  const total = num(raw[amountField]);
  const formatted = cash(raw[amountField]);
  if (formatted && (highlight === null || total !== highlight)) parts.push(`Total ${formatted}`);
  return parts.length ? parts.join('  •  ') : null;
};

const row = (over: Partial<MetricRecordRow> & { title: string }): MetricRecordRow => ({
  subtitle: null,
  trailing: null,
  linkId: null,
  linkTarget: null,
  ...over,
});

const jobWithTotal = (raw: Raw): MetricRecordRow => {
  const id = recordId(raw, 'id');
  return row({ title: ourRef('job', id), trailing: cash(raw.total), linkId: linkable(id), linkTarget: 'job' });
};

const paylogWithTotal = (raw: Raw): MetricRecordRow => {
  const id = recordId(raw, 'id', 'paylogId');
  const numeric = /^\d+$/.test(id);
  return row({
    title: `Paylog #${id}`,
    trailing: cash(raw.total),
    linkId: numeric ? id : null,
    linkTarget: numeric ? 'paylog' : null,
  });
};

const jobIdOnly = (raw: Raw): MetricRecordRow => {
  const id = recordId(raw, 'id', 'jobId');
  return row({ title: ourRef('job', id), linkId: linkable(id), linkTarget: 'job' });
};

const quoteIdOnly = (raw: Raw): MetricRecordRow => {
  // A quote's own id is `jobId` here — same table, same route.
  const id = recordId(raw, 'jobId', 'id');
  return row({ title: ourRef('quote', id), linkId: linkable(id), linkTarget: 'job' });
};

const quoteWithTotal = (raw: Raw): MetricRecordRow => {
  const id = recordId(raw, 'jobId');
  return row({ title: ourRef('quote', id), trailing: cash(raw.total), linkId: linkable(id), linkTarget: 'job' });
};

const driverIdOnly = (raw: Raw): MetricRecordRow => {
  const id = recordId(raw, 'id', 'driverId');
  return row({ title: `Chauffeur #${id}`, linkId: linkable(id), linkTarget: 'driver' });
};

const invoiceBasic = (raw: Raw): MetricRecordRow => {
  const id = recordId(raw, 'id');
  return row({
    title: `Invoice #${id}`,
    subtitle: billingAddress(raw),
    linkId: linkable(id),
    linkTarget: 'invoice',
  });
};

const invoiceTotal = (raw: Raw): MetricRecordRow => {
  const id = recordId(raw, 'id');
  return row({
    title: `Invoice #${id}`,
    subtitle: billingAddress(raw),
    trailing: cash(raw.invoiceTotal),
    linkId: linkable(id),
    linkTarget: 'invoice',
  });
};

const invoiceField = (field: 'received' | 'due') => (raw: Raw): MetricRecordRow => {
  const id = recordId(raw, 'id');
  return row({
    title: `Invoice #${id}`,
    subtitle: invoiceSubtitle(raw, field, 'invoiceTotal'),
    trailing: cash(raw[field]),
    linkId: linkable(id),
    linkTarget: 'invoice',
  });
};

const entity = (
  raw: Raw,
  idKey: string,
  name: (raw: Raw) => string | null,
  fallbackTitle: string,
  target: RecordTarget,
  includeState = false,
): MetricRecordRow => {
  const id = recordId(raw, 'id', idKey);
  return row({
    title: name(raw) ?? fallbackTitle,
    subtitle: contactLine(raw, includeState),
    linkId: linkable(id),
    linkTarget: id === '-' ? null : target,
  });
};

/** Everything dispatch sent, when nothing here knows the shape. */
const generic = (raw: Raw): MetricRecordRow => {
  const parts = Object.entries(raw).map(([key, value]) => `${key}: ${value}`);
  return row({ title: parts.length ? parts.join('  •  ') : '-' });
};

/** The category a subcategory belongs to, tolerating a code that is not one. */
const categoryOf = (code: string): string | null => {
  const known = subcategoryOf(code);
  if (known) return known.category;
  switch (normalize(code)) {
    case 'CUSTOMERS':
    case 'NEW_CUSTOMER':
    case 'CUSTOMER':
      return 'customers';
    case 'AFFILIATES':
    case 'NEW_AFFILIATE':
    case 'AFFILIATE':
      return 'affiliates';
    case 'DRIVERS':
    case 'DRIVER_FORM_SUBMISSIONS':
    case 'APPROVED_DRIVERS':
    case 'PENDING_DRIVERS':
    case 'DRIVER':
      return 'drivers';
    default:
      return null;
  }
};

/** One point's `data`, formatted for the subcategory that produced it. */
export const formatMetricRecords = (code: string, data: Raw[]): MetricRecordRow[] => {
  const rows = Array.isArray(data) ? data.filter((entry) => entry && typeof entry === 'object') : [];
  const sub = normalize(code);

  switch (sub) {
    case 'JOBS_TOTAL':
      return rows.map(jobWithTotal);
    case 'INVOICE_TOTAL':
      return rows.map(invoiceTotal);
    case 'INVOICE_RECEIVED':
      return rows.map(invoiceField('received'));
    case 'INVOICE_DUE':
      return rows.map(invoiceField('due'));
    default:
      break;
  }

  // Every dispatch bucket answers with jobs — the board's own rows — so they
  // read exactly as a jobs bucket does and link at the same route.
  if (JOB_ID_ONLY.has(sub) || sub.startsWith('DISPATCH_')) return rows.map(jobIdOnly);
  if (QUOTE_ID_ONLY.has(sub)) return rows.map(quoteIdOnly);
  if (DRIVER_ID_ONLY.has(sub)) return rows.map(driverIdOnly);
  if (QUOTE_WITH_TOTAL.has(sub)) return rows.map(quoteWithTotal);
  if (INVOICE_BASIC.has(sub)) return rows.map(invoiceBasic);
  if (PAYABLES.has(sub)) {
    // Biggest balance first: these buckets carry plenty of $0 rows, and read in
    // dispatch's own order they bury the amounts worth acting on.
    return [...rows]
      .sort((a, b) => (num(b.total) ?? 0) - (num(a.total) ?? 0))
      .map(jobWithTotal);
  }

  switch (categoryOf(code)) {
    case 'customers':
      return rows.map((raw) => entity(raw, 'customerId', personOrCompany, 'Customer', 'customer'));
    case 'affiliates':
      return rows.map((raw) => entity(raw, 'affiliateId', affiliateName, 'Affiliate', 'affiliate'));
    case 'drivers':
      return rows.map((raw) => entity(raw, 'driverId', personOrCompany, 'Chauffeur', 'driver', true));
    case 'paylog':
      return rows.map(paylogWithTotal);
    default:
      return rows.map(generic);
  }
};
